/**
 * The "Today's Session" card on the dashboard — the first place the engine
 * becomes visible to a user.
 *
 * Seam: `#recent-list` exists only on the dashboard, which `dashboard-layout.js`
 * already relies on as a precise dashboard signal. We insert ahead of the card
 * that contains it, so the prescription sits above "Recent Workouts" — what you
 * are about to do outranks what you already did.
 *
 * Every row shows the number *and the reason*. A prescription with no
 * justification is indistinguishable from a guess, and the whole product bet is
 * that this is not a guess.
 */

import { onRendered } from '../../runtime/legacy.js';
import { getRecentExercises } from './history.js';
import { prescribeFor } from './prescribe.js';
import { summariseWithSetCount, explain, isColdStart } from './format.js';

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

function row({ name, summary, reason, cold }) {
  return `
    <div class="today-session-row">
      <div class="today-session-lift">
        <div class="today-session-name">${escapeHtml(name)}</div>
        <div class="today-session-why">${escapeHtml(reason)}</div>
      </div>
      <div class="today-session-target${cold ? ' is-cold' : ''}">${summary ? escapeHtml(summary) : '—'}</div>
    </div>`;
}

function buildCard() {
  const exercises = getRecentExercises();
  if (!exercises.length) return null;

  const rows = exercises.map(({ exerciseId, name, muscleGroup }) => {
    const result = prescribeFor(exerciseId, { muscleGroup });
    return row({
      name,
      summary: isColdStart(result) ? null : summariseWithSetCount(result),
      reason: explain(result),
      cold: isColdStart(result),
    });
  });

  const card = document.createElement('div');
  card.className = 'card today-session-card';
  card.innerHTML = `
    <div class="card-header">
      <h3 class="card-title">Today's Session</h3>
      <a href="#/workout/new" class="btn btn-ghost btn-sm">Start</a>
    </div>
    <div class="today-session-list">${rows.join('')}</div>`;
  return card;
}

export function installTodaySession() {
  onRendered('#recent-list', (list) => {
    // Deliberately NOT gated on demo mode. The seeded demo history is exactly
    // the corpus this card is meant to read, and demo mode is what an App Store
    // reviewer sees — hiding the flagship feature from them would be perverse.
    const anchor = list.closest('.card');
    if (!anchor || !anchor.parentElement) return;
    if (anchor.parentElement.querySelector('.today-session-card')) return;

    const card = buildCard();
    if (card) anchor.parentElement.insertBefore(card, anchor);
  });
}
